import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { webFormsService, PublicWebForm, WebFormField } from '@/services/web-forms.service';

type Status = 'loading' | 'ready' | 'submitting' | 'submitted' | 'failed' | 'not_found';

/**
 * Public-facing renderer for forms built in WebFormBuilder. Mounted at
 * `/embed/forms/:formUid` outside the authenticated layout — anyone with the
 * link can view it and submit a lead.
 */
export default function PublicForm() {
  const { formUid } = useParams<{ formUid: string }>();
  const [form, setForm] = useState<PublicWebForm | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    if (!formUid) { setStatus('not_found'); return; }
    webFormsService.getPublic(formUid)
      .then((f) => {
        setForm(f);
        // Pre-seed values for select fields with their first option for UX
        const seed: Record<string, string> = {};
        for (const fld of f.fields) {
          if (fld.type === 'select' && fld.options?.length) seed[fld.key] = '';
        }
        setValues(seed);
        setStatus(f.isActive ? 'ready' : 'not_found');
      })
      .catch(() => setStatus('not_found'));
  }, [formUid]);

  const set = (k: string, v: string) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !formUid) return;

    // Required-field validation
    for (const f of form.fields) {
      if (f.required && !(values[f.key] ?? '').trim()) {
        setErrorMsg(`${f.label} is required.`);
        return;
      }
    }
    setErrorMsg('');
    setStatus('submitting');
    try {
      const body = {
        fields: values,
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        submission_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
      const r = await webFormsService.submitPublic(formUid, body);
      if (r.ok) setStatus('submitted');
      else { setErrorMsg('Submission failed. Please try again.'); setStatus('failed'); }
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.message ?? 'Submission failed. Please try again.');
      setStatus('failed');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen w-full bg-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-soft">
        {status === 'loading' && (
          <div className="px-8 py-14 text-center text-sm text-text-muted">Loading…</div>
        )}

        {status === 'not_found' && (
          <div className="px-8 py-14 text-center">
            <AlertCircle size={32} className="mx-auto mb-3 text-text-muted" />
            <h2 className="font-display text-lg font-semibold text-text-primary mb-1">Form not available</h2>
            <p className="text-sm text-text-muted">
              This form has been removed or is no longer accepting responses.
            </p>
          </div>
        )}

        {status === 'submitted' && (
          <div className="px-8 py-14 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-success">
              <CheckCircle size={24} />
            </div>
            <h2 className="font-display text-lg font-semibold text-text-primary mb-1">Thank you!</h2>
            <p className="text-sm text-text-muted">
              Your response was received. Someone will be in touch shortly.
            </p>
          </div>
        )}

        {(status === 'ready' || status === 'submitting' || status === 'failed') && form && (
          <form onSubmit={submit} className="px-8 py-7">
            <h1 className="font-display text-xl font-bold text-text-primary mb-1 tracking-tight">
              {form.name}
            </h1>
            {form.description && (
              <p className="text-sm text-text-muted mb-5 leading-relaxed">{form.description}</p>
            )}

            <div className="space-y-3.5">
              {form.fields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={values[f.key] ?? ''}
                  onChange={(v) => set(f.key, v)}
                  disabled={status === 'submitting'}
                />
              ))}
            </div>

            {errorMsg && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="mt-6 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-on-primary shadow-soft transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Submitting…' : 'Submit'}
            </button>

            <p className="mt-4 text-center text-2xs text-text-muted">
              Powered by SlightorCRM
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

interface FieldRowProps {
  field: WebFormField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, value, onChange, disabled }) => {
  const baseInputClass =
    'w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text-primary placeholder:text-text-muted ' +
    'transition-colors hover:border-border-strong focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft ' +
    'disabled:opacity-60';

  return (
    <label className="block">
      <span className="mb-1 block text-[13px] font-medium text-text-secondary">
        {field.label}
        {field.required && <span className="ml-0.5 text-primary">*</span>}
      </span>

      {field.type === 'textarea' ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={3}
          className={`${baseInputClass} py-2.5`}
        />
      ) : field.type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`${baseInputClass} h-10`}
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          inputMode={field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : 'text'}
          autoComplete={field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : 'off'}
          className={`${baseInputClass} h-10`}
        />
      )}
    </label>
  );
};
